import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getGlobalAmbassadors,
  getDestacadosYleyendas,
  getActiveAmbassadors,
  ambassadorCriteria,
  ambassadorBenefits,
  ambassadorScoreActions,
  ambassadorDashboardStats,
  getStatusConfig,
  formatAmbassadorCount,
  type AmbassadorEntry,
} from '@/mocks/rfwAmbassadors';

function AmbassadorCard({ ambassador, size = 'md' }: { ambassador: AmbassadorEntry; size?: 'lg' | 'md' | 'sm' }) {
  const statusConfig = getStatusConfig(ambassador.status);

  if (size === 'sm') {
    return (
      <div className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70 hover:border-background-300/70 transition-all duration-200">
        <div className="relative shrink-0">
          <img src={ambassador.photoUrl} alt={ambassador.name} className="w-10 h-10 rounded-full object-cover object-top" />
          <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold ${statusConfig.bgColor} ${statusConfig.textColor} border-2 border-background-50`}>
            {ambassador.globalRank}
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-foreground-950 truncate">{ambassador.name}</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className={`text-[9px] font-medium ${statusConfig.textColor}`}>{statusConfig.emoji} {statusConfig.label}</span>
            <span className="text-[9px] text-foreground-400">{ambassador.radioFrequency}</span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-sm font-bold text-foreground-950">{ambassador.totalScore}</p>
          <p className="text-[9px] text-foreground-400">pts</p>
        </div>
      </div>
    );
  }

  if (size === 'lg') {
    return (
      <div className={`rounded-2xl border overflow-hidden transition-all duration-300 hover:border-emerald-200/60 bg-background-50 ${ambassador.globalRank <= 3 ? 'border-emerald-200/40' : 'border-background-200/70'}`}>
        <div className="p-5 text-center">
          <div className="relative inline-block mb-3">
            <img src={ambassador.photoUrl} alt={ambassador.name} className={`w-20 h-20 rounded-2xl object-cover object-top border-2 ${ambassador.globalRank === 1 ? 'border-emerald-400' : ambassador.globalRank === 2 ? 'border-amber-300' : ambassador.globalRank === 3 ? 'border-sky-400' : 'border-background-200'}`} />
            <div className={`absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-extrabold text-background-50 ${ambassador.globalRank === 1 ? 'bg-emerald-500' : ambassador.globalRank === 2 ? 'bg-amber-500' : ambassador.globalRank === 3 ? 'bg-sky-500' : 'bg-foreground-700'}`}>
              #{ambassador.globalRank}
            </div>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-0.5">{ambassador.name}</p>
          <p className="text-[10px] text-foreground-500 mb-2">{ambassador.username} · {ambassador.city}, {ambassador.country}</p>
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold ${statusConfig.bgColor} ${statusConfig.textColor}`}>
            {statusConfig.emoji} {statusConfig.label}
          </span>
          <div className="mt-3 text-center">
            <p className="text-xl font-extrabold text-emerald-600">{ambassador.totalScore}</p>
            <p className="text-[9px] text-foreground-400">puntos</p>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-1 text-center">
            <div>
              <p className="text-[10px] font-bold text-foreground-950">{ambassador.invitaciones}</p>
              <p className="text-[8px] text-foreground-400">Invit.</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-foreground-950">{ambassador.antiguedad}m</p>
              <p className="text-[8px] text-foreground-400">Antig.</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-foreground-950">{ambassador.reputacion}</p>
              <p className="text-[8px] text-foreground-400">Reput.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 p-3.5 rounded-xl bg-background-50 border border-background-200/70 hover:border-background-300/70 transition-all duration-200">
      <div className="relative shrink-0">
        <img src={ambassador.photoUrl} alt={ambassador.name} className="w-11 h-11 rounded-xl object-cover object-top" />
        <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold ${ambassador.globalRank <= 3 ? 'bg-emerald-500 text-white' : 'bg-foreground-700 text-background-50'}`}>
          {ambassador.globalRank}
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <p className="text-xs font-semibold text-foreground-950 truncate">{ambassador.name}</p>
          <span className={`text-[9px] font-medium ${statusConfig.textColor}`}>{statusConfig.emoji}</span>
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[9px] text-foreground-500">{ambassador.radioFrequency}</span>
          <span className="text-[9px] text-foreground-400">· {ambassador.invitaciones} invit.</span>
        </div>
      </div>
      <div className="text-right shrink-0">
        <p className="text-sm font-bold text-emerald-600">{ambassador.totalScore}</p>
      </div>
    </div>
  );
}

export default function EmbajadoresSection() {
  const [activeTab, setActiveTab] = useState<'destacados' | 'activos' | 'beneficios'>('destacados');
  const globalAmbassadors = getGlobalAmbassadors(10);
  const destacados = getDestacadosYleyendas();
  const activos = getActiveAmbassadors();

  const tabs = [
    { id: 'destacados' as const, label: 'Embajadores Destacados', icon: 'ri-star-line' },
    { id: 'activos' as const, label: 'Embajadores Activos', icon: 'ri-user-heart-line' },
    { id: 'beneficios' as const, label: 'Beneficios', icon: 'ri-gift-2-line' },
  ];

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold mb-4">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
            SISTEMA DE EMBAJADORES RFW
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Promotores Oficiales del <span className="text-emerald-600">Crecimiento</span> Orgánico
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            Actividad, Participación, Invitaciones, Antigüedad y Reputación. Los Embajadores no solo escuchan — representan, invitan y hacen crecer RFW desde sus comunidades.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
          {[
            { label: 'Embajadores', value: ambassadorDashboardStats.totalEmbajadores.toString(), icon: 'ri-user-heart-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Destacados', value: ambassadorDashboardStats.destacados.toString(), icon: 'ri-star-line', color: 'text-amber-600 bg-amber-50' },
            { label: 'Leyendas', value: ambassadorDashboardStats.leyendas.toString(), icon: 'ri-vip-crown-line', color: 'text-rose-600 bg-rose-50' },
            { label: 'Invitaciones', value: formatAmbassadorCount(ambassadorDashboardStats.totalInvitaciones), icon: 'ri-user-add-line', color: 'text-violet-600 bg-violet-50' },
            { label: 'Radios Nuevas', value: ambassadorDashboardStats.radiosNuevas.toString(), icon: 'ri-radio-line', color: 'text-sky-600 bg-sky-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-center mb-8">
          <div className="inline-flex bg-background-50 rounded-full p-1 border border-background-200/70">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-emerald-500 text-white shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'destacados' && (
          <div className="space-y-6">
            {/* Podium Top 3 */}
            <div className="flex items-end justify-center gap-3 md:gap-5 px-2 mb-8">
              {destacados[1] && (
                <AmbassadorCard ambassador={destacados[1]} size="lg" />
              )}
              {destacados[0] && (
                <AmbassadorCard ambassador={destacados[0]} size="lg" />
              )}
              {destacados[2] && (
                <AmbassadorCard ambassador={destacados[2]} size="lg" />
              )}
            </div>

            {/* Rest 4-10 destacados y activos */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-full bg-background-200 flex items-center justify-center">
                  <i className="ri-list-check text-xs text-foreground-500"></i>
                </div>
                <p className="text-xs font-bold text-foreground-700 uppercase tracking-wider">Posiciones 4 - 10</p>
              </div>
              {globalAmbassadors.slice(3, 10).map((amb) => (
                <AmbassadorCard key={amb.id} ambassador={amb} size="sm" />
              ))}
            </div>

            {/* Criterios de puntuación */}
            <div className="mt-8 p-5 rounded-2xl bg-background-50 border border-background-200/70">
              <h4 className="text-sm font-bold text-foreground-950 flex items-center gap-2 mb-4">
                <i className="ri-scales-line text-emerald-500"></i>
                ¿Cómo se Evalúa a un Embajador RFW?
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                {ambassadorCriteria.map((c) => (
                  <div key={c.key} className="text-center p-3 rounded-xl bg-background-100">
                    <div className={`w-8 h-8 rounded-full ${c.bgColor} flex items-center justify-center mx-auto mb-2`}>
                      <i className={`${c.icon} ${c.color} text-sm`}></i>
                    </div>
                    <p className="text-[10px] font-bold text-foreground-950">{c.label}</p>
                    <p className="text-[9px] text-foreground-500">{(c.weight * 100).toFixed(0)}%</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'activos' && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
              <p className="text-xs font-bold text-foreground-700 uppercase tracking-wider">Todos los Embajadores Activos</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {activos.slice(0, 9).map((amb) => (
                <div key={amb.id} className="p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-emerald-200/60 transition-all duration-200">
                  <div className="flex items-center gap-3 mb-3">
                    <img src={amb.photoUrl} alt={amb.name} className="w-12 h-12 rounded-full object-cover object-top" />
                    <div>
                      <p className="text-sm font-bold text-foreground-950">{amb.name}</p>
                      <p className="text-[10px] text-foreground-500">{amb.city}, {amb.country}</p>
                    </div>
                    <span className={`ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold ${getStatusConfig(amb.status).bgColor} ${getStatusConfig(amb.status).textColor}`}>
                      {getStatusConfig(amb.status).emoji} {getStatusConfig(amb.status).label}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    {ambassadorCriteria.map((c) => (
                      <div key={c.key} className="p-2 rounded-lg bg-background-100">
                        <p className="text-sm font-bold text-foreground-950">{amb[c.key as keyof AmbassadorEntry] as number}</p>
                        <p className="text-[8px] text-foreground-400">{c.label}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-200/60">
                    <span className="text-[10px] text-foreground-500">{amb.radioFrequency}</span>
                    <span className="text-sm font-bold text-emerald-600">{amb.totalScore} pts</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'beneficios' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {ambassadorBenefits.map((benefit) => (
                <div key={benefit.id} className="p-5 rounded-2xl bg-background-50 border border-background-200/70 text-center">
                  <div className={`w-12 h-12 rounded-2xl ${benefit.bgColor} flex items-center justify-center mx-auto mb-3`}>
                    <span className="text-xl">{benefit.emoji}</span>
                  </div>
                  <h4 className={`text-sm font-bold ${benefit.textColor} mb-1`}>{benefit.name}</h4>
                  <p className="text-[10px] text-foreground-500 leading-relaxed mb-3">{benefit.description}</p>
                  <span className={`inline-block px-2.5 py-0.5 rounded-full text-[9px] font-medium ${benefit.bgColor} ${benefit.textColor}`}>
                    {benefit.levels}
                  </span>
                </div>
              ))}
            </div>

            {/* Score Actions */}
            <div className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
              <h4 className="text-sm font-bold text-foreground-950 flex items-center gap-2 mb-4">
                <i className="ri-flashlight-line text-emerald-500"></i>
                Acciones que Suman Puntos de Embajador
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {ambassadorScoreActions.slice(0, 6).map((action) => (
                  <div key={action.key} className={`flex items-center gap-2.5 p-3 rounded-xl ${action.bgColor}`}>
                    <i className={`${action.icon} ${action.color} text-sm`}></i>
                    <div>
                      <p className="text-[10px] font-bold text-foreground-950">+{action.points} pts</p>
                      <p className="text-[9px] text-foreground-500">{action.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-10">
          <Link
            to="/embajadores"
            className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap"
          >
            <i className="ri-user-heart-line"></i>
            Ver Todos los Embajadores
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Regla Final */}
        <div className="mt-10 p-6 rounded-2xl bg-emerald-100/70 border border-emerald-200/50 text-center">
          <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-user-heart-line text-lg text-white"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">
            Los Embajadores ayudan al crecimiento orgánico de RFW.
          </p>
          <p className="text-xs text-foreground-600 max-w-lg mx-auto">
            No es un título — es una responsabilidad. Cada Embajador representa a RFW en su comunidad, invita nuevos oyentes y radios, participa en cada evento y construye la reputación de la plataforma. El crecimiento orgánico de RFW depende de sus Embajadores. Son la cara visible de nuestra comunidad en el mundo real.
          </p>
        </div>
      </div>
    </section>
  );
}